import { useEffect, useState } from "react";
import { subscribeLoading } from "../lib/api";

/**
 * GlobalLoadingBar
 * ----------------
 * A slim progress bar fixed to the top of the viewport that activates
 * whenever any axios request is in flight. Non-intrusive, animates in/out.
 * Also shows a tiny pulsing "..." label on the right while loading.
 */
export default function GlobalLoadingBar() {
  const [pending, setPending] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let hideTimer;
    const unsub = subscribeLoading((n) => {
      setPending(n);
      if (n > 0) {
        clearTimeout(hideTimer);
        setVisible(true);
      } else {
        // Delay hiding briefly so the bar completes its animation
        hideTimer = setTimeout(() => setVisible(false), 350);
      }
    });
    return () => {
      unsub();
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes amc-loader-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes amc-loader-dots {
          0%, 20% { content: ""; }
          40% { content: "."; }
          60% { content: ".."; }
          80%, 100% { content: "..."; }
        }
        .amc-loader-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: rgba(30, 58, 138, 0.12);
          z-index: 100000;
          overflow: hidden;
          pointer-events: none;
          opacity: 0;
          transition: opacity 220ms ease-out;
        }
        .amc-loader-bar.amc-visible { opacity: 1; }
        .amc-loader-bar::before {
          content: "";
          position: absolute;
          inset: 0;
          width: 40%;
          background: linear-gradient(90deg,
            transparent 0%,
            #1e3a8a 20%,
            #3b82f6 50%,
            #1e3a8a 80%,
            transparent 100%);
          animation: amc-loader-slide 1.1s ease-in-out infinite;
          box-shadow: 0 1px 6px rgba(59, 130, 246, 0.4);
        }
        .amc-loader-pill {
          position: fixed;
          top: 10px;
          right: 12px;
          padding: 4px 10px;
          background: rgba(30, 58, 138, 0.92);
          color: white;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.3px;
          z-index: 100000;
          box-shadow: 0 4px 14px rgba(30, 58, 138, 0.25);
          display: flex;
          align-items: center;
          gap: 6px;
          pointer-events: none;
          opacity: 0;
          transform: translateY(-6px);
          transition: opacity 180ms ease-out, transform 180ms ease-out;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif;
        }
        .amc-loader-pill.amc-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .amc-loader-pill::after {
          content: "...";
          display: inline-block;
          min-width: 14px;
          animation: amc-loader-dots 1.1s steps(4, end) infinite;
        }
        .amc-loader-spark {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #bfdbfe;
          box-shadow: 0 0 10px #93c5fd;
          animation: amc-loader-pulse 0.9s ease-in-out infinite;
        }
        @keyframes amc-loader-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.6); opacity: 0.5; }
        }
      `}</style>
      <div
        className={`amc-loader-bar${visible ? " amc-visible" : ""}`}
        data-testid="global-loading-bar"
        aria-hidden="true"
      />
      <div
        className={`amc-loader-pill${visible ? " amc-visible" : ""}`}
        data-testid="global-loading-pill"
        role="status"
        aria-live="polite"
        aria-label={`Carregando ${pending} ${pending === 1 ? "requisição" : "requisições"}`}
      >
        <span className="amc-loader-spark" aria-hidden="true" />
        <span>Carregando</span>
      </div>
    </>
  );
}
